<template>
  <VanPopup
    :show="visible"
    :position="popupPosition"
    :style="{ height: '60vh', background: 'transparent' }"
    round
    @close="handlePopupClose"
  >
    <div class="sku-wrapper" :data-platform="useIsMobile().value ? 'mobile' : 'pc'">
      <template v-if="!detail || loading">
        <div class="loading">
          <van-loading type="spinner" color="#DD3131" size="32px" />
        </div>
      </template>

      <template v-else>
        <div class="goods-info">
          <div
            class="goods-img-box"
            @click="handleSkuImageClick"
          >
            <img class="goods-img" v-lazy="$cvWebpFn(currentSelectedSku?.images[0] || useImg1pxBase64().value)">
          </div>
          <div class="goods-inner-info">
            <div class="desc">
              <div class="price">
                {{ $currencySymbol() }} {{ $toThousandsFn(skuPrice) }}
              </div>
              <div
                v-show="skuPrice !== currentSelectedSku?.listPrice"
                class="underline-price"
              >
                {{  $currencySymbol() }} {{  $toThousandsFn(currentSelectedSku?.listPrice) }}
              </div>
            </div>

            <div class="stock-state">
              <span class="stock-state-text">{{ stockStateText }}</span>

              <VanStepper
                v-model="quantity"
                integer
                @change="handleQtyChange"
              />
            </div>
          </div>
        </div>
        
        <div class="sku-inner-wrapper">
          <div class="specs-info-box">
            <div
              v-for="(item, idx) in detail?.specs"
              :key="idx"
              class="specs-info-item"
            >
              <div class="label info-item-spec">
                <span>{{ item.name }}</span>
                <span
                  v-if="item.name?.toLocaleLowerCase().indexOf('size') >= 0 && detail.sizeChartPicture"
                  class="size-guide"
                  @click="handleSizeGuideClick(detail.sizeChartPicture)"
                >
                  Size Guide
                  <van-icon name="arrow" />
                </span>
              </div>
              <div class="spec-value">
                <div
                  v-for="(specValue, specIdx) in item.values"
                  :key="`${idx}_${specIdx}`"
                  class="spec-btn-box"
                  v-show="!specValue.disabledRemoved"
                >
                  <div
                    :class="detail.specs.length <= 1 ? 'sku-btn big-sku-btn' : 'sku-btn'"
                    :select="specSelectIndexs[idx] === `${idx}_${specIdx}` && '1'"
                    @click="handleSkuBtnClick(idx, specIdx)"
                  >
                    <div class="sku-img-box" v-if="specValue.image">
                      <img
                        v-lazy="$cvWebpFn(specValue.image, { w: 80, removeTag: true })"
                        class="sku-img"
                      />
                    </div>
                    <span class="sku-value-text">{{ specValue.value }}</span>
                  </div>

                  <ClientOnly>
                    <div v-if="specValue.disabled || specValue.temporaryDisabled" class="sold-out-mark">Sold out</div>
                  </ClientOnly>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="option-box switch-sku-option-box">
          <!-- 到货通知订阅 -->
          <button
            v-if="showArrivalReminderBtn"
            type="button"
            id="buyNow"
            :class="currentSelectedSku?.isArrivalReminder ? 'opt-btn red-btn disabled-opt-btn' : 'opt-btn red-btn'"
            @click="handleArrivalReminderClick"
          >
            {{ currentSelectedSku?.isArrivalReminder ? 'Cancel Reminder' : 'Arrival Reminder' }}
          </button>
          <button 
            v-else
            :class="
              optionBtnState
                ? 'opt-btn red-btn disabled-opt-btn'
                : 'opt-btn red-btn'
            "
            :disabled="optionBtnState"
            @click="handleAddCartClick('Add To Cart')"
          >Add To Cart</button>
        </div>
      </template>
      <van-icon name="close" class="close-btn" @click="handlePopupClose" />
    </div>
  </VanPopup>
</template>
  
<script setup lang="ts">
import { type IGifts } from '@/components/KGifts/Card.vue'
import { addCartItem, getGiftsBySkuId, getInventoryBySkuId, getListingByIdV2, putSubscription, type ISkuItemParams, type ISubscriptionParams } from '@/api/models/product'
import { getShipingCartTotal } from '@/api/models/userInfo'
import { initSkuId, disableOutOfStockSpecs, updateStatus, getShowArrivalReminder} from '@/pages-listing-detail/mixins';
import { webClickFn } from '@/plugins/bi-sa.client'
import { showImagePreview, type PopupPosition } from 'vant';

interface IProps {
  visible: boolean,
  listingId: number,
  defaultSkuId: number,
  popupPosition: PopupPosition,
}
interface IEmits {
  (event: 'update:visible', visible: boolean): void

  (event: 'add-cart:success'): void
  (event: 'update:ok', data: any): void
}
const props = withDefaults(defineProps<IProps>(), { visible: false, popupPosition: 'bottom' })
const emits = defineEmits<IEmits>()
const { $SENSORS_EVENT, $setShoppingCartLaconicList, $getCountry, $sensors } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const quantity = ref<number>(1)
const trackBaseData: any = {}

const gifts = ref<IGifts[]>([])
const giftsCacheMap: { [key:string]: IGifts[] } = {}
const initGift = async() => {
  if(!currentSelectedSku.value?.isGift) {
    return
  }

  if(giftsCacheMap[currentSkuId.value]) {
    gifts.value = giftsCacheMap[currentSkuId.value]
  } else {
    const { giftActivities = [] } = await getGiftsBySkuId(currentSkuId.value)

    gifts.value = giftActivities
    giftsCacheMap[currentSkuId.value] = giftActivities
  }
}

const loading = ref<boolean>(false)
const detail = ref<ProductV3.ListingDetailV3>()
const currentSkuId = ref<number>(0)
const currentSelectedSku = computed(() => detail.value?.skus.find(item => item.skuId === currentSkuId.value))
const skuActivateInfo = ref<ProductV3.SelectedSkuActivity>()

const skuPrice = computed(() => {
  return skuActivateInfo.value?.activityPrice || currentSelectedSku.value?.salePrice
})

const currentSkuDeliveryType = computed(() => {
  if(!currentSelectedSku.value) {
    return 'fbk'
  }

  const { fbkAvailable, gsAvailable, dsAvailable } = currentSelectedSku.value!.inventory

  if(fbkAvailable > 0) { return 'fbk' }
  if(gsAvailable > 0) { return 'gs' }
  if(dsAvailable > 0) { return 'ds' }

  return 'fbk'
}) 
const initListingDetail = async() => {
  try {
    loading.value = true
    const res = await getListingByIdV2({ productId: props.listingId, skuId: props.defaultSkuId || '' })

    detail.value = res
    skuActivateInfo.value = res.selectedSkuActivity
    currentSkuId.value = props.defaultSkuId || initSkuId(props.defaultSkuId, res.skus) || 0
    
    initSpecSelectIndexs(detail.value!.specs)
    const colorSpec = detail.value!.specs.find(v=> v.name.toUpperCase().indexOf('COLOR') >= 0)

    if(colorSpec) {
      const { values, pid } = colorSpec
      values.forEach(val => {
        const _sku = detail.value!.skus.find((c: ProductV3.Sku) => c.key.indexOf(`${pid}:${val.pvid}`) >= 0)

        if(_sku && _sku.images?.length > 0) {
          val.image = _sku.images[0] || ''
        }
      }) 
    }
  } catch(e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.visible,
  async (val) => {
    if(!val || !props.listingId) { return }

    await initListingDetail()
    await initGift()

    if(detail.value) {
      const {skus=[], specs=[]} = detail.value
      // 提前将无库存的规格标记
      disableOutOfStockSpecs(specs, skus)
      // 更新与当前选中规格相关规格的状态
      if(currentSelectedSku.value && detail.value) {

        updateStatus(
          {
            skuKey: currentSelectedSku.value.key,
            skus: skus,
            specs: specs
          }
        )
      }
    }
  }
)

const specSelectIndexs = ref<string[]>([])
const maxQuantity = computed<number>(() => {
  if(!currentSelectedSku.value) {
    return 0
  }

  const { fbkAvailable, dsAvailable, gsAvailable, fbkInventories } = currentSelectedSku.value.inventory

  if(fbkAvailable > 0) {
    return Math.max(...fbkInventories.map(c => c.available))
  }

  return Math.max(dsAvailable, gsAvailable)
})

const stockStateText = computed<string>(() => {
  if(!currentSelectedSku.value) { return 'Out of Stock' }

  const { fbkInventories, gsAvailable, fbkAvailable, dsAvailable } = currentSelectedSku.value.inventory

  let maxStock = Math.max(...fbkInventories.map(c => c.available))
  if(maxStock <= 0) {
    maxStock = Math.max(gsAvailable, Math.max(...fbkInventories.map(c => c.available)), dsAvailable)
  }

  if(maxStock <= 0) return 'Out of Stock'
  if(maxStock <= 5) return 'Low Stock'
  return 'In Stock'
})
// 操作按钮是否可用（加入购物车、下单）
const optionBtnState = computed<boolean>(() => {
  const storeStatus = detail.value?.storeStatus
  if(!currentSelectedSku.value || !detail.value?.storeStatus) {
    return true
  }

  let { inventory, status } = currentSelectedSku.value
  // 活动库存

  const { gsAvailable, fbkAvailable, dsAvailable } = inventory
  if(status < 0 || Math.max(gsAvailable, fbkAvailable, dsAvailable) <= 0) {
    return true
  }

    // status === 2 歇业
  if(storeStatus === 2) {
    // 如果是 FBK，则可以下单
    return currentSkuDeliveryType.value === 'fbk' && fbkAvailable > 0
  }

  return false
})

const stepperMaxVal = computed<number>(() => {
  const defaultLimitNum = $getCountry()?.orderConfig?.qtyLimit || 30

  return maxQuantity.value > defaultLimitNum ? defaultLimitNum : maxQuantity.value
})

const showPreviewImage = (src: string) => {
  handlePopupClose()
  showImagePreview({
    images: [src],
    closeable: true,
    onClose: () => {
      emits('update:visible', true)
    }
  });
}

const handleSizeGuideClick = (sizeChartPicture: string) => {
  showPreviewImage(sizeChartPicture)
}

const handleAddCartClick = async(btnName: string) => {  
  if(!currentSelectedSku.value) {
    return showToast('Please choose a specification.')
  }

  if(!useUserInfo().value?.accessToken) {
    return router.push(`/login?redirect=${encodeURI(route.fullPath)}`)
  }

  try {
    const { articleId, authorCode, referCode, source = '' } = route.query || {}
    const currentSkuId = currentSelectedSku.value!.skuId
    const [
      internalLatestTrafficSourceModule = '',
      internalLatestTrafficSourceType = '',
      insideLatestTrafficSourceValue = ''
    ] = (source as string).split('-')
    const isAd = trackBaseData.isAd || false
    const skuParams: ISkuItemParams = {
      sku: currentSkuId,
      count: quantity.value,
      activityId: 0,
      activityType: 0,
      referCode,
      authorCode,
      referSource: 'Listing',
      referSourceId: articleId,
      internalLatestTrafficSourceModule,
      internalLatestTrafficSourceType,
      insideLatestTrafficSourceValue,
      isAd
    }

    const addCartRes = await addCartItem(skuParams)
    if (addCartRes.code === 0) {
      showToast('Successfully added to shopping cart.')
      emits('add-cart:success')
      // BI埋点
      webClickFn(101, btnName, true)
    } else {
      // BI埋点
      webClickFn(101, btnName, false, 'api_code_error', addCartRes.msg || addCartRes.message)
    }
    
    const {  imageUrls, listingId, title } = detail.value!

    $setShoppingCartLaconicList({
      listingId: listingId,
      skuId: currentSkuId || -1,
      productImage: imageUrls[0] || '',
      productTitle: title,
      spec: currentSelectedSku.value?.spec || '',
      salePrice: skuPrice.value || 0,
    })

    getShipingCartTotal().then((r: any) => useShopingCartTotal().value = r?.count || 0)

    handlePopupClose()
  } catch (e) {
    console.error(e)
  }
}

const handlePopupClose = () => {
  emits('update:visible', false)
}


const initSpecSelectIndexs = (specs: ProductV3.Specs[]) => {
  if(!detail.value) return

  const skuSpecs: { name: string, id: string }[] = []
  if(currentSelectedSku.value) {
    const { key } = currentSelectedSku.value
    const specAry = key.split(';')
    specAry.forEach((spec: string, idx: number) => {
      const [specBase, specVal] = spec.split(':')
      const baseIdx = specs.findIndex((c) => c.pid === Number(specBase))

      if(baseIdx >= 0) {
        const { values, name, pid } =  specs[baseIdx]
        
        const valIdx = values.findIndex((c) => c.pvid === Number(specVal))

        specSelectIndexs.value[idx] = `${baseIdx}_${valIdx}`
        
        if(valIdx >= 0) {
          skuSpecs.push({ name: `${name}:${values[valIdx].value}`, id: `${pid}:${values[valIdx].pvid}` })
        }
      }
    })
  } else {
    specSelectIndexs.value = new Array(specs.length).fill('').map((v: any, idx: number) => `${idx}_0`)

    for(let i=0; i < specs.length; i++) {
      const { name, values, pid } = specs[i]

      if(values.length > 0) {
        skuSpecs.push({ name: `${name}:${values[0].value}`, id: `${pid}:${values[0].pvid}` })
      }
    }
  }
}

// 订阅到货通知按钮显示
const showArrivalReminderBtn = computed(() => {
  // const { currentSelectedSku, skuActivateInfo } = props
  
  return getShowArrivalReminder(currentSelectedSku.value)
})


// 到货通知订阅
const handleArrivalReminderClick = async() => {
  if(!currentSelectedSku.value) {
    return
  }
  const { isArrivalReminder, skuId } = currentSelectedSku.value

  try {
    await showConfirmDialog({
      message: isArrivalReminder 
        ? 'Do you want to cancel your subscription?'
        : 'Do you want to confirm that "Arrival Reminder" is enabled? Enable this function to receive alert when your desired products are restocked.',
    })
  } catch(e) {
    return
  }
  
  const params: ISubscriptionParams = {
    source_type: 3,
    is_subscribe: !isArrivalReminder,
    sku_id: skuId
  }

  try {
    await putSubscription(params)
    showToast('Notification set successfully!')

    const curSku = detail.value?.skus.find(item => item.skuId === currentSkuId.value)
    curSku && (curSku.isArrivalReminder = params.is_subscribe)
  } catch(e: any) {
    console.error(e)
    showToast(e.message)
  }
}

const handleSkuBtnClick = async (index: number, idx: number) => {
  specSelectIndexs.value[index] = `${index}_${idx}`

  const skuKeys: string[] = []
  const skuSpecs: string[] = []
  specSelectIndexs.value.forEach((c: string) => {
    const [baseIdx, valIdx] = c.split('_').map((c: string) => Number(c))
    
    const spec = detail.value!.specs[baseIdx]
    const specValue = spec.values[valIdx]
    skuKeys.push(`${spec.pid}:${specValue.pvid}`)

    skuSpecs.push(`${spec.name}:${specValue.value}`)
  })

  const sku = detail.value!.skus.find((c: ProductV3.Sku) => {
    let inSku = true
    for(let i=0; i < skuKeys.length; i++) {
      inSku = c.key.indexOf(skuKeys[i]) >= 0

      if(!inSku) { break }
    }

    return inSku
  })

  await handleUpdateSkuClick(skuSpecs.join('/'), sku)
}

const updateSkuInventory = async() => {
  if(!currentSkuId.value) {
    return
  }

  const sku = detail.value!.skus.find((c: ProductV3.Sku) => c.skuId === currentSkuId.value)

  if(sku) {
    const params: { activity_type?: number, activate_id?: number | string } = {}

    if(route.query?.activateId) {
      params['activate_id'] = route.query.activateId as string
    }

    const { inventory, activity } = await getInventoryBySkuId(sku?.skuId, params)
    skuActivateInfo.value = activity
    Object.assign(sku.inventory, inventory)
  }
}

const handleUpdateSkuClick = async(skuKey: string, sku?: ProductV3.Sku) => {
  if(sku?.skuId === currentSkuId.value) {
    return
  }
  updateStatus({ skuKey, skus: detail.value!.skus, specs: detail.value!.specs })

  currentSkuId.value = sku?.skuId || 0

  if(currentSkuId.value <= 0) { return }

  await Promise.all([
    updateSkuInventory(),
    initGift()
  ])
}

const handleQtyChange = (e: number) => {
  let currentQty = e

  if(e > stepperMaxVal.value) {
    currentQty = stepperMaxVal.value
    showToast(`We've adjusted the quantity to ${stepperMaxVal.value}.`)
  } else if(e < 1) {
    currentQty = 1
  } else {
    currentQty = e
  }
  
  nextTick(() => { quantity.value = currentQty })
}

const handleSkuImageClick = () => {
  if(currentSelectedSku.value?.images[0]) {
    showPreviewImage(currentSelectedSku.value?.images[0])
  }
}
</script>
  
<style lang="scss" scoped>
.sku-wrapper[data-platform="pc"] {
  width: 1200px;
  padding: 30px;
}
.sku-wrapper {
  height: 100%;
  background-color: #fff;
  position: relative;
  .loading{
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sku-inner-wrapper {
    padding: 20px;
    overflow-y: auto;
    height: calc(100% - 100px - 200px);
    &::-webkit-scrollbar {
      display: none; /* Chrome Safari */
    }
  }
  
  .close-btn{
    position: absolute;
    top: 30px;
    right: 30px;
    color: #999;
    font-size: 46px;
  }
}
.option-box {
  box-shadow: 0 -1px 1px 0 rgba(211, 214, 215, 0.38);
  height: 76px;
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  text-align: center;
  .opt-btn {
    line-height: 76px;
    color: #fff;
    border-radius: 40px;
    font-size: 28px;
    width: 100%;
    border: none;
    outline: none;
    font-weight: 700;
  }
  .disabled-opt-btn {
    color: #fff !important;
    line-height: 72px;
    border: 1px solid transparent !important;
    box-sizing: border-box;
    background: gray !important;
  }
  .red-btn{
    background-color: #e04545;
  }
}
.switch-sku-option-box{
  border: 3px solid var(--default_color);
  border-radius: 76px;
  display: flex;
  overflow: hidden;
  .opt-btn{
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .disabled-opt-btn{
    border-radius: 0;
  }
}
.switch-sku-option-box[disabled="true"] { border-color: transparent;}

// sku info style
.goods-info {
  display: flex;
  padding: 20px 20px 0 20px;
  .goods-img-box {
    height: 160px;
    width: 160px;
    position: relative;
    border: 1px solid #efefef;
    border-radius: 8px;
    padding: 8px;
    .goods-img {
      width: 100%;
      height: 100%;
      border-radius: 8px;
      object-fit: contain;
    }
    .enlarge-img {
      position: absolute;
      top: 0;
      right: 0;
      width: 40px;
      height: 40px;
    }
  }
  .goods-inner-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 0 10px 20px;
    font-size: 32px;
    .desc {
      display: flex;
      align-items: end;
      padding-top: 20px;
      .price {
        color: #ff2720;
        font-weight: 600;
      }
      .underline-price {
        margin-left: 20px;
        color: #999;
        font-size: 24px;
        text-decoration: line-through;
      }
    }
    .stock-state {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .stock-state-text{
        color: #cacaca;
        font-size: 24px;
      }
      .van-stepper {
        :deep() .van-stepper__plus::after  {
          width: 3px;
        }
      }
    }
  }
}

.specs-info-box{
  margin: 10px 0;
  .specs-info-item{
    margin-bottom: 40px;
    .label { color: #333; font-weight: 600;font-size: 28px;}
    .info-item-spec{
      display: flex;align-items: center;justify-content: space-between;
      .size-guide{ color: #3b9dff;font-weight: 400; }
    }
    .spec-value{
      .spec-btn-box{
        margin: 15px 15px 0 0;
        display: inline-block;
        position: relative;
      }
      
      .sku-btn{
        min-width: 100px;
        padding: 4px 15px;
        border-radius: 2px;
        overflow: hidden;
        border: 1px solid transparent;
        background-color: #f5f5f5;
        color: #333;
        display: flex;
        min-height: 70px;
        align-items: center;
        .sku-img-box{
          height: 70px;
          margin-right: 10px;
        }
        .sku-img{
          height: 100%;
          width: 70px;
          object-fit: contain;
        }
        .sku-value-text {
          font-size: 28px;
        }
      }
      .big-sku-btn{
        flex-direction: column;
        padding: 0;
        .sku-img-box{
          height: 160px;
          margin-right: 0;
        }
        .sku-img{
          width: 160px;
          height: 100%;
          object-fit: contain;
          margin-right: 0;
        }
        .sku-value-text{
          display: inline-block;
          padding: 10px;
        }
      }
      .sku-btn[select="1"]{
        border-color: var(--default_color);
        color: var(--default_color);
        background-color: #fff;
      }
      .sold-out-mark{
        position: absolute;
        top: -10px;
        right: -8px;
        background-color: #cacaca;
        color: #fff;
        border-radius: 25px;
        // line-height: 25px;
        padding: 4px 8px;
        font-size: 16px;
        overflow: hidden;
      }
    }
  }
}
</style>
